import { Component, OnInit } from '@angular/core';
import { TitleService } from '../../shared/services/title.service';
import { SharedDataService } from '../../shared/services/shared-data.service';
import { ClientBillingBackupFileConfigurationService } from '../../shared/api/services/client-billing-backup-file-configuration.service';
import { BackupFileColumn } from '../../shared/api/models/backup-file-column';
import { ClientBillingBackupFileConfiguration, Party } from '../../shared/api/models';
import { matchClientPartyRow } from '../../shared/classes/helpers';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';

@Component({
  selector: 'backup-configure',
  templateUrl: './backup-configure.component.html',
  styleUrls: ['./backup-configure.component.scss']
})

export class BackupConfigureComponent implements OnInit {
  public showSpinner: boolean = true;
  public aviClientId: string = null;
  public showDefaultConfigMessage = false;
  private currentConfig;
  public matchClientPartyRow = matchClientPartyRow;


  public clientConfigFields = {
    available: Array<GroupItem>(),
    active: Array<GroupItem>(),
    allDafaults: Array<GroupItem>(),
  };

  constructor(
    public sharedDataService: SharedDataService,
    private _titleService: TitleService,
    private _clientBillingBackupFileConfigurationService: ClientBillingBackupFileConfigurationService,
    private route: ActivatedRoute,
    private router: Router) {
    this._titleService.Subtitle = 'Client Config Backup Fields';
  }

  async ngOnInit() {

    this.route.params.subscribe(params => {
      this.aviClientId = params['clientId'];
      this.setSelectedClient();
    });

    try {
      let defaultConfig = await this._clientBillingBackupFileConfigurationService.GetDefaultBackupFileConfiguration().toPromise();
      this.clientConfigFields.allDafaults = GroupItemFactory.generateByBackupFileColumns(defaultConfig);
    } catch (error) {
      this.sharedDataService.HandleError(error);
    }
  }

  setActiveFields() {
    this.clientConfigFields.active = GroupItemFactory.generateByBackupFileColumns(this.currentConfig.columns);
  }

  setAvailableFields() {
    this.clientConfigFields.available = this.clientConfigFields.allDafaults.filter(item => {
      return !this.clientConfigFields.active.find(innerItem => {
        let sameHeader = innerItem.representedObject.headerValue === item.representedObject.headerValue;
        let sameFieldName = innerItem.representedObject.fieldName === item.representedObject.fieldName;
        return sameHeader && sameFieldName;
      });
  });
  }


  async addClientUsageDataColumns()
  {
    let customDataFields = await this._clientBillingBackupFileConfigurationService.GetBackupFileCustomUsageFields(
      {clientId: this.aviClientId}
    ).toPromise();
    let usageDataGroupItems = GroupItemFactory.generateByBackupFileColumns(customDataFields);
    usageDataGroupItems.forEach(item => this.clientConfigFields.allDafaults.push(item));
  }

  async setSelectedClient() {
    if (!(this.aviClientId)) { return; }

    this.showSpinner = true;

    try {
      let result = await this._clientBillingBackupFileConfigurationService.GetClientBackupFileConfigByAviClientId(
        { aviClientId: this.aviClientId }
      ).toPromise();

      this.showSpinner = false;
      this.showDefaultConfigMessage = false;
      this.currentConfig = result || this.generateEmptyClientConfig();

      // Below function will be implemented later
      // await this.addClientUsageDataColumns();

      this.setActiveFields();
      this.setAvailableFields();
    } catch (error) {
      this.sharedDataService.HandleError(error);
      this.showSpinner = false;
    }
  }

  generateEmptyClientConfig(): ClientBillingBackupFileConfiguration {
    let clientConfig = {} as ClientBillingBackupFileConfiguration;
    clientConfig.aviClientId = this.aviClientId;
    clientConfig.columns = [];
    clientConfig.fileType = "XLSX";
    this.notifyAboutEmptyConfig();
    return clientConfig;
  }

  notifyAboutEmptyConfig() {
    window.alert("This client does not have a config setup yet. Until you save one, the backup process will use the default fields.");
    this.showDefaultConfigMessage = true;
  }

  async createClientConfigFields() {
    this.showSpinner = true;
    this.currentConfig.columns = GroupItemFactory.toBackupFileColumnsArray(this.clientConfigFields.active);
    let resp = await this._clientBillingBackupFileConfigurationService.PushClientBackUpConfiguration({ backupConfig: this.currentConfig }).toPromise();
    this.setActiveFields();
    this.setAvailableFields();
    this.showSpinner = false;
    this.showDefaultConfigMessage = false;
  }
  //#region UI Handles
  moveToActive(itemToMove: GroupItem) {
    let idx = this.clientConfigFields.available.findIndex((item) => item === itemToMove);
    let toMove = this.clientConfigFields.available.splice(idx, 1)[0];
    this.clientConfigFields.active.push(toMove);
  }

  moveToAvailable(itemToMove: GroupItem)  {
    let idx = this.clientConfigFields.active.findIndex((item) => item === itemToMove);
    let toMove = this.clientConfigFields.active.splice(idx, 1)[0];
    this.clientConfigFields.available.push(toMove);
  }

  changePosition(arrayToChange, itemToMove, isMoveUp, isMax) {
    let idx = arrayToChange.findIndex((item) => item === itemToMove);
    let length = arrayToChange.length;
    let isAtTop = idx === 0;
    let isAtBot = idx === length;
    let possibleMove = (!isAtTop && isMoveUp) || (!isAtBot && !isMoveUp);
    if (!possibleMove) { return; }

    arrayToChange.splice(idx, 1);
    if (isMoveUp && isMax) {
      arrayToChange.unshift(itemToMove);
    } else if (isMoveUp && !isMax) {
      arrayToChange.splice(idx - 1, 0, itemToMove);
    } else if (!isMoveUp && isMax) {
      arrayToChange.push(itemToMove);
    } else if (!isMoveUp && !isMax) {
      arrayToChange.splice(idx + 1, 0, itemToMove);
    }

  }

  onSelectedClientChange() {
    this.setSelectedClient();
  }

  onActiveChangePosition(itemToMove, isMoveUp, isMax, evt?) {
    this.changePosition(this.clientConfigFields.active, itemToMove, isMoveUp, isMax);
    if (evt) {
      evt.stopPropagation();
      evt.preventDefault();
    }
    return false;
  }

  onAvailableChangePosition(itemToMove, isMoveUp, isMax, evt?) {
    this.changePosition(this.clientConfigFields.available, itemToMove, isMoveUp, isMax);
    if (evt) {
      evt.stopPropagation();
      evt.preventDefault();
    }
    return false;
  }

  onAddToActive() {
    let selected = this.clientConfigFields.available.filter((item) => item.selected);

    selected.forEach((item) => {
      this.moveToActive(item);
      item.selected = false;
    });
  }

  onAddToAvailable() {
    let selected = this.clientConfigFields.active.filter((item) => item.selected);
    selected.forEach((item) => {
      this.moveToAvailable(item);
      item.selected = false;
    });
  }

  onSelectAllActive(isSelected) {
    this.clientConfigFields.active.forEach(item => item.selected = isSelected);
  }

  onSelectAllAvailable(isSelected) {
    this.clientConfigFields.available.forEach(item => item.selected = isSelected);
  }

  onSubmit() {
    this.createClientConfigFields();
  }
  //#endregion
}

class GroupItem {
  representedObject: any;
  itemName: string;
  selected: boolean;
}

class GroupItemFactory {
  static generateByStringArray(array) {
    return array.map(stringItem => {
      let item = new GroupItem();
      item.itemName = stringItem;
      item.selected = false;
      item.representedObject = stringItem;
      return item;
    });
  }

  static generateByBackupFileColumns(array: Array<BackupFileColumn>) {
    return array.map(colItem => {
      let item = new GroupItem();
      item.itemName = colItem.headerValue,
      item.selected = false;
      item.representedObject = colItem;
      return item;
    });
  }

  static toBackupFileColumnsArray(array: Array<GroupItem>) {
    return array.map(gItem => gItem.representedObject as BackupFileColumn);
  }
}
